import React, { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertTriangle, RefreshCw, Trash2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Admin view of ShopKeeper AI publish / generate failures shopkeepers have hit.
 * Helps diagnose why "product publishing is failing" — shows the exact error,
 * who hit it, and when (both 4xx validation messages and 5xx server errors).
 */
export default function AdminMunimjiErrors() {
  const [data, setData] = useState({ errors: [], total: 0, server_errors: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/munimji-errors");
      setData(d || { errors: [], total: 0, server_errors: 0 });
    } catch (e) {
      toast.error("Could not load ShopKeeper AI errors");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const clearAll = async () => {
    if (!window.confirm("Clear all logged ShopKeeper AI errors?")) return;
    try {
      await api.delete("/admin/munimji-errors");
      setData({ errors: [], total: 0, server_errors: 0 });
      toast.success("Cleared");
    } catch (e) {
      toast.error("Could not clear");
    }
  };

  const badge = (code) => {
    const server = code >= 500;
    return (
      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${server ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
        {code} {server ? "Server error" : "Blocked"}
      </span>
    );
  };

  return (
    <div className="space-y-4" data-testid="admin-munimji-errors">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" /> ShopKeeper AI publish failures
          </h3>
          <p className="text-sm text-slate-500">
            {data.total} logged · <span className="text-red-600 font-semibold">{data.server_errors} server errors (500)</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="rounded-xl" onClick={load} data-testid="admin-munimji-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button variant="outline" size="sm" className="rounded-xl text-red-600 border-red-200" onClick={clearAll} data-testid="admin-munimji-clear">
            <Trash2 className="w-4 h-4 mr-1" /> Clear
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : data.errors.length === 0 ? (
        <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl p-4">✓ No publishing failures logged. ShopKeeper AI is healthy.</div>
      ) : (
        <div className="space-y-2">
          {data.errors.map((e) => (
            <Card key={e.id} className="rounded-xl border-slate-100" data-testid={`admin-munimji-row-${e.id}`}>
              <CardContent className="p-3">
                <div className="flex items-center gap-2 flex-wrap">
                  {badge(e.status_code)}
                  <span className="text-xs font-semibold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">{e.stage}</span>
                  <span className="text-xs text-slate-500">{e.user_name}{e.user_mobile ? ` · ${e.user_mobile}` : ""}</span>
                  <span className="text-xs text-slate-400 ml-auto">{e.created_at ? new Date(e.created_at).toLocaleString() : ""}</span>
                </div>
                <div className="text-sm text-slate-800 mt-1.5">{e.detail}</div>
                {e.product_title ? <div className="text-xs text-slate-400 mt-0.5">Product: “{e.product_title}”</div> : null}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
